/**
 * meta/daily-login.js — the 30-day login streak calendar (GDD 8.7).
 *
 * Rewards come straight from `DAILY_LOGIN_CALENDAR`, which transcribes the GDD
 * table exactly: 5 tickets a day to start, milestones at 7/14/21, and a day-30
 * payout of 50 tickets, 5 Stars and an exclusive cosmetic that rotates each
 * time the calendar is completed.
 *
 * The streak rule is the whole design: claim on consecutive days and the day
 * number climbs; miss one and it drops back to day 1. "Consecutive" is measured
 * in `save.today()` days, so `__cc.time.advanceDays(1)` continues a streak and
 * `advanceDays(2)` breaks it — the same arithmetic real time performs.
 *
 * @module meta/daily-login
 */

import { DAILY_LOGIN_CALENDAR, MONTHLY_LOGIN_COSMETICS, EVENTS, META_EVENTS } from '../constants.js';
import { save, economy } from '../core/save.js';
import { bus } from '../core/events.js';
import { logCall } from '../core/testmode.js';
import { cosmetics as metaCosmetics } from './cosmetics-catalog.js';
import { dailySlice } from './state.js';

/** The last day of the calendar; claiming it resets the month. */
export const CALENDAR_LENGTH = 30;

/**
 * Which calendar day the next claim would land on.
 * @param {number} today
 * @param {{ streak: number, lastClaimDay: number }} d
 * @returns {number} 1–30.
 */
function nextDayNumber(today, d) {
  const continued = d.lastClaimDay === today - 1;
  return continued ? Math.min(CALENDAR_LENGTH, d.streak + 1) : 1;
}

export const dailyLogin = {
  /**
   * What is due right now, if anything. Signature preserved from the stub —
   * `main.js` calls this on boot.
   * @returns {{ day: number, tickets: number, stars: number, milestone?: string,
   *             cosmetic?: boolean }|null} Null when today is already claimed.
   */
  check() {
    logCall('meta', 'dailyLogin.check', []);
    const today = save.today();
    const d = dailySlice();
    if (d.lastClaimDay === today) return null;
    return DAILY_LOGIN_CALENDAR[nextDayNumber(today, d)] || null;
  },

  /** @returns {boolean} Is a reward waiting? */
  isDue() {
    return dailySlice().lastClaimDay !== save.today();
  },

  /**
   * Claim today's reward.
   *
   * @returns {{ day, tickets, stars, milestone?, cosmetic?: string|null,
   *             streakReset: boolean, calendarComplete: boolean }|null}
   *   Null if today has already been claimed.
   */
  claim() {
    logCall('meta', 'dailyLogin.claim', []);
    const today = save.today();
    const d = dailySlice();
    if (d.lastClaimDay === today) return null;

    const streakReset = d.lastClaimDay !== today - 1 && d.lastClaimDay >= 0;
    const day = nextDayNumber(today, d);
    const reward = DAILY_LOGIN_CALENDAR[day];
    if (!reward) return null;

    economy.grant({ tickets: reward.tickets, stars: reward.stars }, 'daily-login');

    const calendarComplete = day === CALENDAR_LENGTH;
    let cosmetic = null;

    if (calendarComplete) {
      // GDD 8.7 — the monthly exclusive differs each time the calendar is run.
      cosmetic = MONTHLY_LOGIN_COSMETICS[d.calendarsCompleted % MONTHLY_LOGIN_COSMETICS.length];
      metaCosmetics.unlock(cosmetic);
      d.calendarsCompleted += 1;
      d.streak = 0;
      d.claimedDays = [];
    } else {
      d.streak = day;
      if (!d.claimedDays.includes(day)) d.claimedDays.push(day);
    }

    d.lastClaimDay = today;
    save.markDirty();

    const parts = [`+${reward.tickets} tickets`];
    if (reward.stars) parts.push(`+${reward.stars}★`);
    if (cosmetic) {
      const def = metaCosmetics.def(cosmetic);
      parts.push(def ? def.name : cosmetic);
    }
    bus.emit(EVENTS.UI_TOAST, {
      text: `Day ${day}: ${parts.join(' · ')}`,
      kind: 'prize',
      duration: calendarComplete ? 4600 : 2800,
    });
    bus.emit(EVENTS.AUDIO_PLAY, {
      name: calendarComplete ? 'daily-monthly' : (reward.milestone ? 'daily-milestone' : 'daily-claim'),
      day,
    });
    bus.emit(META_EVENTS.DAILY_CLAIMED, {
      day, tickets: reward.tickets, stars: reward.stars, cosmetic,
    });

    return {
      day,
      tickets: reward.tickets,
      stars: reward.stars,
      milestone: reward.milestone,
      cosmetic,
      streakReset,
      calendarComplete,
    };
  },

  /**
   * The 30-day grid a calendar UI renders.
   * @returns {Array<{ day, tickets, stars, milestone?, state: 'claimed'|'today'|'future' }>}
   */
  calendar() {
    const d = dailySlice();
    const today = save.today();
    const dueDay = d.lastClaimDay === today ? -1 : nextDayNumber(today, d);
    return DAILY_LOGIN_CALENDAR.slice(1).map((entry) => {
      let state = 'future';
      if (d.claimedDays.includes(entry.day)) state = 'claimed';
      if (entry.day === dueDay) state = 'today';
      return { ...entry, state };
    });
  },

  /** Everything a UI or test needs in one call. */
  state() {
    const d = dailySlice();
    const today = save.today();
    const due = d.lastClaimDay !== today;
    return {
      day: today,
      streak: d.streak,
      lastClaimDay: d.lastClaimDay,
      claimedDays: [...d.claimedDays],
      calendarsCompleted: d.calendarsCompleted,
      due,
      nextDay: due ? nextDayNumber(today, d) : null,
      nextReward: due ? DAILY_LOGIN_CALENDAR[nextDayNumber(today, d)] : null,
      nextExclusive: MONTHLY_LOGIN_COSMETICS[
        d.calendarsCompleted % MONTHLY_LOGIN_COSMETICS.length
      ],
    };
  },
};

export default dailyLogin;
